import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';
import {
  IconBrain, IconCompose, IconLightbulb, IconChatBubble,
  IconChartBar, IconChartPie, IconSearch, IconSparkles,
  IconBookOpen, IconClock, IconDocument, IconCog,
} from './icons/SidebarIcons';

const primaryTabs = [
  { id: 'brain-dump', path: '/brain-dump', icon: IconBrain, labelKey: 'sidebar.nav.brainDump' },
  { id: 'compose', path: '/compose', icon: IconCompose, labelKey: 'sidebar.nav.composeIdeas' },
  { id: 'coach', path: '/coach', icon: IconLightbulb, labelKey: 'sidebar.nav.contentCoach' },
  { id: 'reply', path: '/reply', icon: IconChatBubble, labelKey: 'sidebar.nav.replyGuy' },
] as const;

const moreTabs = [
  { id: 'analytics', path: '/analytics', icon: IconChartBar, labelKey: 'sidebar.nav.analytics' },
  { id: 'algo', path: '/algo', icon: IconChartPie, labelKey: 'sidebar.nav.algoAnalyzer' },
  { id: 'researcher', path: '/researcher', icon: IconSearch, labelKey: 'sidebar.nav.accountResearcher' },
  { id: 'inspiration', path: '/inspiration', icon: IconSparkles, labelKey: 'sidebar.nav.inspiration' },
  { id: 'education', path: '/education', icon: IconBookOpen, labelKey: 'sidebar.nav.education' },
  { id: 'history', path: '/history', icon: IconClock, labelKey: 'sidebar.nav.history' },
  { id: 'drafts', path: '/drafts', icon: IconDocument, labelKey: 'sidebar.nav.drafts' },
  { id: 'admin', path: '/admin', icon: IconCog, labelKey: 'sidebar.nav.admin' },
] as const;

const MoreIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={`w-5 h-5 flex-shrink-0 ${className || ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="5" cy="12" r="1.5" />
    <circle cx="12" cy="12" r="1.5" />
    <circle cx="19" cy="12" r="1.5" />
  </svg>
);

export const MobileNav: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useT();
  const [moreOpen, setMoreOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isInMoreSection = moreTabs.some(tab => tab.path === location.pathname);

  // Close menu on outside click
  useEffect(() => {
    if (!moreOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [moreOpen]);

  // Close on route change
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  return (
    <>
      {/* More menu overlay */}
      {moreOpen && (
        <div className="fixed inset-0 bg-black/40 z-40 lg:hidden animate-fade-in" />
      )}

      <nav className="fixed bottom-0 left-0 right-0 z-sidebar lg:hidden" ref={menuRef}>
        {/* More menu panel */}
        {moreOpen && (
          <div className="absolute bottom-full left-0 right-0 bg-surface-1 border-t border-border rounded-t-2xl shadow-2xl animate-slide-up overflow-hidden">
            <div className="p-2 grid grid-cols-4 gap-1">
              {moreTabs.map((tab) => {
                const isActive = location.pathname === tab.path;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => navigate(tab.path)}
                    className={cn(
                      'flex flex-col items-center justify-center gap-1 py-3 px-1 rounded-xl bg-transparent border-none cursor-pointer transition-all duration-150',
                      isActive
                        ? 'text-brand bg-brand/10'
                        : 'text-text-muted hover:bg-surface-3 hover:text-text-primary active:scale-95'
                    )}
                  >
                    <Icon className={cn('w-5 h-5', isActive ? 'text-brand' : '')} />
                    <span className="text-[10px] font-medium leading-tight text-center">
                      {t(tab.labelKey)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Bottom tab bar */}
        <div className="bg-surface-1/95 backdrop-blur-xl border-t border-border-subtle">
          <div className="flex items-stretch">
            {primaryTabs.map((tab) => {
              const isActive = location.pathname === tab.path;
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => navigate(tab.path)}
                  className={cn(
                    'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 px-1 bg-transparent border-none cursor-pointer transition-colors duration-150',
                    isActive ? 'text-brand' : 'text-text-muted'
                  )}
                >
                  <Icon className={cn('w-5 h-5', isActive ? 'text-brand' : '')} />
                  <span className="text-[9px] font-medium truncate max-w-full">
                    {t(tab.labelKey)}
                  </span>
                </button>
              );
            })}
            {/* More button */}
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={cn(
                'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 px-1 bg-transparent border-none cursor-pointer transition-colors duration-150',
                moreOpen || isInMoreSection ? 'text-brand' : 'text-text-muted'
              )}
            >
              <MoreIcon className={cn(moreOpen || isInMoreSection ? 'text-brand' : '')} />
              <span className="text-[9px] font-medium truncate max-w-full">
                {t('sidebar.nav.more')}
              </span>
            </button>
          </div>
          {/* Safe area for iOS */}
          <div className="h-[env(safe-area-inset-bottom)]" />
        </div>
      </nav>
    </>
  );
};
